import React, { FC, useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  HumanTimeActivityResponse,
  HumanTimeActivityItem,
} from '@dreamai/types';
import api from '../../api';
import './human-time-activity-modal.scss';

interface HumanTimeActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function formatMinutes(totalMinutes: number): string {
  if (totalMinutes <= 0) return '0 mins';
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  if (mins > 0) parts.push(`${mins} min${mins !== 1 ? 's' : ''}`);
  return parts.join(' ');
}

function formatHours(hours: number): string {
  const totalMinutes = Math.round(hours * 60);
  return formatMinutes(totalMinutes);
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

const TASK_TYPE_LABELS: Record<string, string> = {
  phone_call: 'Phone call',
  web_action: 'Web task',
  research: 'Research',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  accepted: 'In progress',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const ActivityRow: FC<{ item: HumanTimeActivityItem }> = ({ item }) => {
  const isTopUp = item.type === 'topup';
  const isCancelled = item.status === 'cancelled';

  return (
    <div className={`activity-row ${item.type}`}>
      <div className='activity-icon-col'>
        <div
          className={`activity-icon ${isTopUp ? 'topup' : item.status || 'task'}`}
        >
          {isTopUp ? (
            <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
              <path
                d='M7 1V13M1 7H13'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
              />
            </svg>
          ) : (
            <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
              <path
                d='M7 3.5V7L9.5 8.5'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <circle
                cx='7'
                cy='7'
                r='5.5'
                stroke='currentColor'
                strokeWidth='1.5'
              />
            </svg>
          )}
        </div>
      </div>

      <div className='activity-content'>
        <p className='activity-label'>{item.label}</p>
        <div className='activity-meta'>
          {item.taskType && (
            <span className='activity-tag task-type'>
              {TASK_TYPE_LABELS[item.taskType] || item.taskType}
            </span>
          )}
          {item.status && (
            <span className={`activity-tag status ${item.status}`}>
              {STATUS_LABELS[item.status] || item.status}
            </span>
          )}
          {item.detail && (
            <span className='activity-detail'>{item.detail}</span>
          )}
        </div>
      </div>

      <div className='activity-amount-col'>
        <span
          className={`activity-amount ${isTopUp ? 'positive' : isCancelled ? 'neutral' : 'negative'}`}
        >
          {isTopUp ? '+' : isCancelled ? '' : '−'}
          {isCancelled ? '—' : formatHours(item.hours)}
        </span>
        <span className='activity-date'>{formatDate(item.date)}</span>
      </div>
    </div>
  );
};

const HumanTimeActivityModal: FC<HumanTimeActivityModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<HumanTimeActivityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchActivity = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const res = await api.get<HumanTimeActivityResponse>(
        '/credits/human-time-activity',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setData(res.data);
    } catch (err) {
      console.error('Error fetching human time activity:', err);
      setError('Failed to load activity.');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (isOpen) {
      fetchActivity();
    }
  }, [isOpen, fetchActivity]);

  if (!isOpen) return null;

  return (
    <div className='ht-activity-overlay' onClick={onClose}>
      <div className='ht-activity-modal' onClick={(e) => e.stopPropagation()}>
        <div className='ht-activity-header'>
          <p className='ht-activity-title'>Human Time</p>
          <button className='ht-close-button' onClick={onClose}>
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#374562'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        </div>

        {data && !loading && (
          <div className='ht-activity-summary'>
            <span className='summary-remaining'>
              {formatMinutes(data.remainingMinutes)}
            </span>
            <span className='summary-label'>remaining</span>
          </div>
        )}

        <div className='ht-activity-body'>
          {loading && <div className='ht-activity-loading'>Loading...</div>}

          {error && <div className='ht-activity-error'>{error}</div>}

          {!loading && !error && data && data.activity.length === 0 && (
            <div className='ht-activity-empty'>No Human Time activity yet.</div>
          )}

          {!loading && !error && data && data.activity.length > 0 && (
            <div className='activity-list'>
              {data.activity.map((item, i) => (
                <ActivityRow
                  key={`${item.type}-${item.date}-${i}`}
                  item={item}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HumanTimeActivityModal;
